import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { useOrganizationApi } from '../../hooks/useOrganizationApi';
import { route } from '../../utils/route';
import { Trash2, Plus, MapPin, Settings } from 'lucide-react';
import { ConfirmModal } from '../../components/ConfirmModal';
import { AddressForm } from '../../components/address/AddressForm';
import type {
    OrganizationData,
    OrganizationCompany,
    OrganizationLocation,
    OrganizationMember,
    OrganizationInvitation,
    OrganizationAccountReceivable,
} from '../../types/organization';

interface Props {
    organization: OrganizationData;
}

const ROLE_LABELS: Record<string, string> = {
    owner: 'Propietario',
    admin: 'Administrador',
    buyer: 'Comprador',
    viewer: 'Visualizador',
};

export default function OrganizationIndex({ organization: initial }: Props) {
    const [org, setOrg] = useState<OrganizationData>(initial);
    useEffect(() => { setOrg(initial); }, [initial]);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const { post, isLoading, error, setError } = useOrganizationApi();

    const isManager = org.user_role === 'owner' || org.user_role === 'admin';
    const isBuyer = org.user_role === 'buyer';

    const showSuccess = useCallback((message: string) => {
        setSuccessMessage(message);
        setError(null);
        setTimeout(() => setSuccessMessage(null), 4000);
    }, [setError]);

    const apiCall = useCallback(async (url: string, data: any) => {
        const result = await post(url, data);
        if (result.success && (result as any).organization) {
            setOrg((result as any).organization);
            showSuccess((result as any).message || 'Actualizado.');
        }
        return result;
    }, [post, showSuccess]);

    return (
        <MainLayout>
            <Head>
                <title>Mi Organizacion</title>
                <meta name="robots" content="noindex" />
            </Head>

            <div className="mx-auto max-w-6xl px-4 py-6">
                <PageHeader
                    title="Mi Organizacion"
                    subtitle={isBuyer
                        ? `Tus sucursales y cuentas corrientes en "${org.name}".`
                        : `Gestioná tu organizacion "${org.name}".`
                    }
                />

                {successMessage && (
                    <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                        {successMessage}
                    </div>
                )}
                {error && (
                    <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                <div className="mt-6 space-y-6">
                    {!isBuyer && (
                        <NameSection
                            name={org.name}
                            isManager={isManager}
                            isLoading={isLoading}
                            onSave={(name) => apiCall(route('organization.update-name'), { name })}
                        />
                    )}

                    {!isBuyer && (
                        <CompaniesSection
                            companies={org.companies}
                            isManager={isManager}
                            isLoading={isLoading}
                            onSetDefault={(companyId) => apiCall(route('organization.set-default-company'), { company_id: companyId })}
                            onRemove={(companyId) => apiCall(route('organization.remove-company'), { company_id: companyId })}
                            onAdd={(cuit) => apiCall(route('organization.add-company'), { cuit })}
                            onAssignAR={(companyId, arId) => {
                                setOrg(prev => ({
                                    ...prev,
                                    companies: prev.companies.map(c =>
                                        c.id === companyId
                                            ? {
                                                ...c,
                                                account_receivable_id: arId,
                                                has_account_receivable: arId !== null,
                                                account_receivable_name: arId
                                                    ? prev.companies.find(x => x.account_receivable_id === arId)?.account_receivable_name || null
                                                    : null,
                                                account_receivable_balance: arId
                                                    ? prev.companies.find(x => x.account_receivable_id === arId)?.account_receivable_balance ?? null
                                                    : null,
                                            }
                                            : c
                                    ),
                                }));
                                return apiCall(route('organization.assign-ar'), { company_id: companyId, account_receivable_id: arId });
                            }}
                        />
                    )}

                    <AccountsSection accountReceivables={org.account_receivables} />

                    <LocationsSection
                        locations={org.locations}
                        companies={org.companies}
                        accountReceivables={org.account_receivables}
                        isManager={isManager}
                        isLoading={isLoading}
                        onUpdateLocation={(locationId, companyId, accountReceivableId) => {
                            return apiCall(route('organization.update-location-company'), {
                                location_id: locationId,
                                company_id: companyId,
                                account_receivable_id: accountReceivableId,
                            });
                        }}
                        onDelete={(locationId) => apiCall(route('organization.delete-location'), { location_id: locationId })}
                        onAddSuccess={() => router.reload({ preserveScroll: true })}
                    />

                    {!isBuyer && (
                        <MembersSection
                            members={org.members}
                            locations={org.locations}
                            invitations={org.invitations}
                            isManager={isManager}
                            isLoading={isLoading}
                            onUpdateRole={(userId, role) => apiCall(route('organization.update-member-role'), { user_id: userId, role })}
                            onRemove={(userId) => apiCall(route('organization.remove-member'), { user_id: userId })}
                            onInvite={(email, role, locationIds) => apiCall(route('organization.invite-member'), { email, role, location_ids: locationIds })}
                            onCancelInvitation={(invitationId) => apiCall(route('organization.cancel-invitation'), { invitation_id: invitationId })}
                            onUpdateLocations={(userId, locationIds) => apiCall(route('organization.update-member-locations', { userId }), { location_ids: locationIds })}
                        />
                    )}
                </div>
            </div>
        </MainLayout>
    );
}

/* ── Name Section ─────────────────────────────────────────────────────── */

function NameSection({ name, isManager, isLoading, onSave }: {
    name: string;
    isManager: boolean;
    isLoading: boolean;
    onSave: (name: string) => Promise<any>;
}) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(name);

    const handleSave = async () => {
        if (!value.trim()) return;
        const result = await onSave(value.trim());
        if (result.success) setEditing(false);
    };

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Nombre de la organizacion</h2>
            {!editing ? (
                <div className="flex items-center justify-between">
                    <span className="text-gray-700">{name}</span>
                    {isManager && (
                        <button
                            type="button"
                            onClick={() => { setValue(name); setEditing(true); }}
                            className="text-sm text-green-600 hover:text-green-700 bg-transparent border-none cursor-pointer"
                        >
                            Editar
                        </button>
                    )}
                </div>
            ) : (
                <div className="flex items-center gap-3">
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:border-green-500"
                        disabled={isLoading}
                        maxLength={255}
                    />
                    <Button onClick={handleSave} disabled={isLoading || !value.trim()}>
                        Guardar
                    </Button>
                    <Button variant="secondary" onClick={() => setEditing(false)} disabled={isLoading}>
                        Cancelar
                    </Button>
                </div>
            )}
        </section>
    );
}

/* ── Companies Section ────────────────────────────────────────────────── */

function CompaniesSection({ companies, isManager, isLoading, onSetDefault, onRemove, onAdd, onAssignAR }: {
    companies: OrganizationCompany[];
    isManager: boolean;
    isLoading: boolean;
    onSetDefault: (companyId: number) => Promise<any>;
    onRemove: (companyId: number) => Promise<any>;
    onAdd: (cuit: string) => Promise<any>;
    onAssignAR: (companyId: number, arId: number | null) => Promise<any>;
}) {
    const [confirmRemove, setConfirmRemove] = useState<number | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [cuit, setCuit] = useState('');
    const [cuitName, setCuitName] = useState('');
    const [cuitLoading, setCuitLoading] = useState(false);
    const [cuitError, setCuitError] = useState('');
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    const lookupCuit = useCallback((value: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setCuitName('');
        setCuitError('');
        if (value.length !== 11) return;
        debounceRef.current = setTimeout(async () => {
            setCuitLoading(true);
            try {
                const res = await fetch(`/api/cuit/${value}`);
                if (!res.ok) {
                    setCuitError('CUIT no encontrado en AFIP.');
                    return;
                }
                const data = await res.json();
                if (data.iva_condition === 5) {
                    setCuitError('El CUIT corresponde a un Consumidor Final.');
                    return;
                }
                setCuitName(data.name);
            } catch {
                setCuitError('Error al consultar AFIP.');
            } finally {
                setCuitLoading(false);
            }
        }, 500);
    }, []);

    const handleCuitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 11);
        setCuit(value);
        lookupCuit(value);
    };

    const handleAdd = async () => {
        if (!cuit || cuit.length !== 11) return;
        const result = await onAdd(cuit);
        if (result.success) {
            setCuit('');
            setCuitName('');
            setCuitError('');
            setShowAddForm(false);
        }
    };

    // Collect unique ARs available in the org for the dropdown
    const availableARs = (() => {
        const seen = new Map<number, string>();
        for (const c of companies) {
            if (c.account_receivable_id && c.account_receivable_name) {
                seen.set(c.account_receivable_id, c.account_receivable_name);
            }
        }
        return Array.from(seen, ([id, name]) => ({ id, name }));
    })();

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Razones Sociales</h2>
                {isManager && !showAddForm && (
                    <Button onClick={() => setShowAddForm(true)}>
                        <i className="fa fa-plus mr-1" /> Agregar
                    </Button>
                )}
            </div>

            {showAddForm && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4">
                    <p className="text-sm text-gray-600 mb-3">Ingresá el CUIT para buscar la razón social en AFIP.</p>
                    <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder="Ej: 30712345678"
                                value={cuit}
                                onChange={handleCuitChange}
                                maxLength={11}
                                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors focus:border-green-500 focus:ring-1 focus:ring-green-500 ${cuitError ? 'border-red-300' : 'border-gray-300'}`}
                                disabled={isLoading}
                            />
                            {cuitLoading && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-500 border-t-transparent" />
                                </div>
                            )}
                        </div>
                        <Button onClick={handleAdd} disabled={isLoading || !cuitName}>
                            Agregar
                        </Button>
                        <Button variant="secondary" onClick={() => { setShowAddForm(false); setCuit(''); setCuitName(''); setCuitError(''); }} disabled={isLoading}>
                            Cancelar
                        </Button>
                    </div>
                    {cuitError && <p className="mt-2 text-xs text-red-600">{cuitError}</p>}
                    {cuitName && (
                        <div className="mt-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                            <span className="font-medium">{cuitName}</span>
                        </div>
                    )}
                </div>
            )}
            {companies.length === 0 ? (
                <p className="text-sm text-gray-500">No hay razones sociales asociadas.</p>
            ) : (
                <div className="space-y-3">
                    {companies.map((company) => (
                        <div key={company.id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-800">{company.display_name}</span>
                                    {company.is_default && (
                                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                            Predeterminada
                                        </span>
                                    )}
                                    {isManager && availableARs.length > 0 ? (
                                        <select
                                            value={company.account_receivable_id ?? ''}
                                            onChange={(e) => {
                                                const val = e.target.value ? Number(e.target.value) : null;
                                                onAssignAR(company.id, val);
                                            }}
                                            disabled={isLoading}
                                            className="rounded-lg border border-gray-300 px-2 py-0.5 text-xs focus:outline-none focus:border-green-500 disabled:opacity-50"
                                        >
                                            <option value="">Sin cuenta corriente</option>
                                            {availableARs.map((ar) => (
                                                <option key={ar.id} value={ar.id}>{ar.name}</option>
                                            ))}
                                        </select>
                                    ) : company.has_account_receivable ? (
                                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                                            CC: {company.account_receivable_name}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="mt-0.5 text-xs text-gray-500">
                                    {company.cuit && `CUIT ${company.cuit} · `}
                                    {company.iva_condition_name}
                                </div>
                                {company.name !== company.display_name && (
                                    <div className="text-xs text-gray-400">{company.name}</div>
                                )}
                            </div>
                            {isManager && !company.is_default && (
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => onSetDefault(company.id)}
                                        disabled={isLoading}
                                        className="text-xs text-green-600 hover:text-green-700 bg-transparent border-none cursor-pointer disabled:opacity-50"
                                    >
                                        Predeterminar
                                    </button>
                                    {confirmRemove === company.id ? (
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => { onRemove(company.id); setConfirmRemove(null); }}
                                                disabled={isLoading}
                                                className="text-xs text-red-600 hover:text-red-700 bg-transparent border-none cursor-pointer font-semibold disabled:opacity-50"
                                            >
                                                Confirmar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setConfirmRemove(null)}
                                                className="text-xs text-gray-500 bg-transparent border-none cursor-pointer"
                                            >
                                                No
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setConfirmRemove(company.id)}
                                            disabled={isLoading}
                                            className="text-xs text-red-500 hover:text-red-600 bg-transparent border-none cursor-pointer disabled:opacity-50"
                                        >
                                            Quitar
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

/* ── Accounts Section ────────────────────────────────────────────────── */

function formatBalance(balance: number | null | undefined): string {
    const val = balance ?? 0;
    const abs = Math.abs(val);
    const formatted = abs.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return val < 0 ? `-$${formatted}` : `$${formatted}`;
}

function AccountsSection({ accountReceivables }: { accountReceivables: OrganizationAccountReceivable[] }) {
    if (accountReceivables.length === 0) return null;

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Cuentas Corrientes</h2>
            <div className="space-y-3">
                {accountReceivables.map((ar) => (
                    <Link
                        key={ar.id}
                        href={`/cuenta-corriente/${ar.id}`}
                        className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 hover:bg-gray-100 transition-colors no-underline"
                    >
                        <div>
                            <div className="font-medium text-gray-800">{ar.name}</div>
                        </div>
                        <div className="text-right">
                            <div className={`font-semibold ${(ar.balance ?? 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {formatBalance(ar.balance ?? 0)}
                            </div>
                            <div className="text-xs text-gray-400">Saldo</div>
                        </div>
                    </Link>
                ))}
                {accountReceivables.length > 1 && (
                    <div className="flex items-center justify-between px-4 pt-2 border-t border-gray-200">
                        <div className="font-medium text-gray-600">Total</div>
                        <div className={`font-semibold ${accountReceivables.reduce((sum, ar) => sum + (ar.balance ?? 0), 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatBalance(accountReceivables.reduce((sum, ar) => sum + (ar.balance ?? 0), 0))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

/* ── Locations Section ────────────────────────────────────────────────── */

function LocationsSection({ locations, companies, accountReceivables, isManager, isLoading, onUpdateLocation, onDelete, onAddSuccess }: {
    locations: OrganizationLocation[];
    companies: OrganizationCompany[];
    accountReceivables: { id: number; name: string }[];
    isManager: boolean;
    isLoading: boolean;
    onUpdateLocation: (locationId: number, companyId: number | null, accountReceivableId: number | null) => Promise<any>;
    onDelete: (locationId: number) => Promise<any>;
    onAddSuccess: () => void;
}) {
    const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingLocation, setEditingLocation] = useState<OrganizationLocation | null>(null);

    const sortedLocations = useMemo(
        () => [...locations].sort((a, b) => a.address.localeCompare(b.address)),
        [locations],
    );

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Sucursales</h2>
                {isManager && (
                    <button
                        type="button"
                        onClick={() => setShowAddForm(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-green-700"
                    >
                        <Plus size={16} /> Agregar
                    </button>
                )}
            </div>
            {sortedLocations.length === 0 ? (
                <p className="text-sm text-gray-500">No hay ubicaciones registradas.</p>
            ) : (
                <div className="space-y-3">
                    {sortedLocations.map((location) => (
                        <div key={location.id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-800">{location.address.split(',')[0]}</div>
                                <div className="mt-0.5 text-xs text-gray-500">
                                    {location.account_receivable_name && (
                                        <div>Cuenta Corriente: {location.account_receivable_name}</div>
                                    )}
                                    {location.company_name && (
                                        <div>Razón Social: {location.company_name}{location.company_afip_name && location.company_afip_name !== location.company_name ? ` - ${location.company_afip_name}` : ''}{location.company_cuit ? ` - CUIT ${location.company_cuit}` : ''}</div>
                                    )}
                                </div>
                            </div>
                            {isManager && (
                                <div className="flex items-center gap-1.5 ml-3 shrink-0">
                                    {(companies.length > 1 || accountReceivables.length > 0) && (
                                        <button
                                            type="button"
                                            onClick={() => setEditingLocation(location)}
                                            disabled={isLoading}
                                            className="flex items-center justify-center w-9 h-9 border-none bg-gray-100 rounded-lg text-gray-400 cursor-pointer transition-all duration-150 shrink-0 hover:enabled:bg-blue-50 hover:enabled:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                            title="Configurar sucursal"
                                        >
                                            <Settings size={18} />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setConfirmDelete(location.id)}
                                        disabled={isLoading}
                                        className="flex items-center justify-center w-9 h-9 border-none bg-gray-100 rounded-lg text-gray-400 cursor-pointer transition-all duration-150 shrink-0 hover:enabled:bg-red-50 hover:enabled:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
                                        title="Eliminar sucursal"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={confirmDelete !== null}
                title="Eliminar sucursal"
                message={`¿Confirmás que querés eliminar "${locations.find(l => l.id === confirmDelete)?.address ?? ''}"?`}
                confirmLabel="Sí, eliminar"
                cancelLabel="No, mantener"
                loadingLabel="Eliminando..."
                loading={isLoading}
                onConfirm={() => { if (confirmDelete) { onDelete(confirmDelete); setConfirmDelete(null); } }}
                onCancel={() => setConfirmDelete(null)}
            />

            {editingLocation && (
                <LocationConfigModal
                    location={editingLocation}
                    companies={companies}
                    accountReceivables={accountReceivables}
                    isLoading={isLoading}
                    onSave={async (companyId, arId) => {
                        await onUpdateLocation(editingLocation.id, companyId, arId);
                        setEditingLocation(null);
                    }}
                    onClose={() => setEditingLocation(null)}
                />
            )}

            {showAddForm && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-[fadeIn_150ms_ease-out]"
                    style={{ background: 'rgba(0,0,0,0.5)' }}
                    onClick={() => setShowAddForm(false)}
                >
                    <div
                        className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl animate-[slideUp_200ms_ease-out] sm:p-8"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <h2 className="mb-6 text-xl font-semibold text-gray-800">Agregar sucursal</h2>
                        <AddressForm
                            routes={{
                                resolve: route('api.location.resolve'),
                                create: route('organization.add-location'),
                            }}
                            onSuccess={() => {
                                setShowAddForm(false);
                                onAddSuccess();
                            }}
                            hideMap
                        />
                    </div>
                </div>
            )}
        </section>
    );
}

/* ── Location Config Modal ───────────────────────────────────────────── */

function LocationConfigModal({ location, companies, accountReceivables, isLoading, onSave, onClose }: {
    location: OrganizationLocation;
    companies: OrganizationCompany[];
    accountReceivables: { id: number; name: string }[];
    isLoading: boolean;
    onSave: (companyId: number | null, accountReceivableId: number | null) => Promise<void>;
    onClose: () => void;
}) {
    const [companyId, setCompanyId] = useState<number | null>(location.company_id);
    const [arId, setArId] = useState<number | null>(location.account_receivable_id);

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-[fadeIn_150ms_ease-out]"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={onClose}
        >
            <div
                className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl animate-[slideUp_200ms_ease-out]"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Configurar sucursal</h2>
                <p className="text-sm text-gray-500 mb-5">{location.address.split(',')[0]}</p>

                <div className="space-y-4">
                    {companies.length > 1 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Facturar a</label>
                            <select
                                value={companyId ?? ''}
                                onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : null)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500"
                            >
                                <option value="">Razón social predeterminada</option>
                                {companies.map((c) => (
                                    <option key={c.id} value={c.id}>{c.display_name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {accountReceivables.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cuenta Corriente</label>
                            <select
                                value={arId ?? ''}
                                onChange={(e) => setArId(e.target.value ? Number(e.target.value) : null)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500"
                            >
                                <option value="">Usar la de la razón social</option>
                                {accountReceivables.map((ar) => (
                                    <option key={ar.id} value={ar.id}>{ar.name}</option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-400">
                                Si no elegís una, se usará la cuenta corriente asignada a la razón social.
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={() => onSave(companyId, arId)}
                        disabled={isLoading}
                        className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                        {isLoading ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── Members Section ──────────────────────────────────────────────────── */

function MembersSection({ members, locations, invitations, isManager, isLoading, onUpdateRole, onRemove, onInvite, onCancelInvitation, onUpdateLocations }: {
    members: OrganizationMember[];
    locations: OrganizationLocation[];
    invitations: OrganizationInvitation[];
    isManager: boolean;
    isLoading: boolean;
    onUpdateRole: (userId: number, role: string) => Promise<any>;
    onRemove: (userId: number) => Promise<any>;
    onInvite: (email: string, role: string, locationIds?: number[]) => Promise<any>;
    onCancelInvitation: (invitationId: number) => Promise<any>;
    onUpdateLocations: (userId: number, locationIds: number[]) => Promise<any>;
}) {
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('buyer');
    const [inviteLocationIds, setInviteLocationIds] = useState<number[]>([]);
    const [confirmRemove, setConfirmRemove] = useState<number | null>(null);
    const [locationsMember, setLocationsMember] = useState<OrganizationMember | null>(null);
    const [selectedLocIds, setSelectedLocIds] = useState<number[]>([]);

    const sortedLocations = useMemo(
        () => [...locations].sort((a, b) => a.address.localeCompare(b.address)),
        [locations],
    );

    const handleInvite = async () => {
        if (!inviteEmail.trim()) return;
        const locIds = inviteRole === 'buyer' && inviteLocationIds.length > 0 ? inviteLocationIds : undefined;
        const result = await onInvite(inviteEmail.trim(), inviteRole, locIds);
        if (result.success) {
            setInviteEmail('');
            setInviteRole('buyer');
            setInviteLocationIds([]);
            setShowInviteForm(false);
        }
    };

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Equipo</h2>
                {isManager && !showInviteForm && (
                    <Button onClick={() => setShowInviteForm(true)}>
                        <i className="fa fa-plus mr-1" /> Invitar
                    </Button>
                )}
            </div>

            {/* Invite form */}
            {showInviteForm && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <input
                            type="email"
                            placeholder="Email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            className="rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:border-green-500 sm:col-span-2"
                            disabled={isLoading}
                        />
                        <select
                            value={inviteRole}
                            onChange={(e) => setInviteRole(e.target.value)}
                            className="rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:border-green-500"
                            disabled={isLoading}
                        >
                            <option value="admin">Administrador</option>
                            <option value="buyer">Comprador</option>
                            <option value="viewer">Visualizador</option>
                        </select>
                    </div>
                    {inviteRole === 'buyer' && sortedLocations.length > 0 && (
                        <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sucursales asignadas</label>
                            <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                {sortedLocations.map((loc) => (
                                    <label key={loc.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={inviteLocationIds.includes(loc.id)}
                                            onChange={(e) => {
                                                setInviteLocationIds(prev =>
                                                    e.target.checked
                                                        ? [...prev, loc.id]
                                                        : prev.filter(id => id !== loc.id)
                                                );
                                            }}
                                            className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                        />
                                        {loc.address.split(',')[0]}
                                    </label>
                                ))}
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                Si no seleccionás ninguna, podrá comprar desde cualquier sucursal.
                            </p>
                        </div>
                    )}
                    <div className="mt-3 flex gap-2">
                        <Button onClick={handleInvite} disabled={isLoading || !inviteEmail.trim()}>
                            Enviar invitacion
                        </Button>
                        <Button variant="secondary" onClick={() => setShowInviteForm(false)} disabled={isLoading}>
                            Cancelar
                        </Button>
                    </div>
                </div>
            )}

            {/* Members list */}
            <div className="space-y-3">
                {members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                        <div>
                            <div className="font-medium text-gray-800">
                                {member.name} {member.lastname}
                            </div>
                            <div className="text-xs text-gray-500">{member.email}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            {isManager && member.role !== 'owner' ? (
                                <select
                                    value={member.role}
                                    onChange={(e) => onUpdateRole(member.id, e.target.value)}
                                    disabled={isLoading}
                                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-green-500 disabled:opacity-50"
                                >
                                    <option value="admin">Administrador</option>
                                    <option value="buyer">Comprador</option>
                                    <option value="viewer">Visualizador</option>
                                </select>
                            ) : (
                                <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
                                    {ROLE_LABELS[member.role] || member.role}
                                </span>
                            )}
                            {isManager && member.role === 'buyer' && sortedLocations.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setLocationsMember(member);
                                        setSelectedLocIds([...member.assigned_location_ids]);
                                    }}
                                    disabled={isLoading}
                                    className="flex items-center justify-center gap-1 px-2 h-9 border-none bg-gray-100 rounded-lg text-gray-400 cursor-pointer transition-all duration-150 shrink-0 hover:enabled:bg-green-50 hover:enabled:text-green-600 disabled:opacity-40 disabled:cursor-not-allowed text-xs"
                                    title="Restringir ubicaciones"
                                >
                                    <MapPin size={16} />
                                    {member.assigned_location_ids.length > 0
                                        ? member.assigned_location_ids.length
                                        : null}
                                </button>
                            )}
                            {isManager && member.role !== 'owner' && (
                                <button
                                    type="button"
                                    onClick={() => setConfirmRemove(member.id)}
                                    disabled={isLoading}
                                    className="flex items-center justify-center w-9 h-9 border-none bg-gray-100 rounded-lg text-gray-400 cursor-pointer transition-all duration-150 shrink-0 hover:enabled:bg-red-50 hover:enabled:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
                                    title="Quitar miembro"
                                >
                                    <Trash2 size={18} />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Pending invitations */}
            {invitations.length > 0 && (
                <div className="mt-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Invitaciones pendientes</h3>
                    <div className="space-y-2">
                        {invitations.map((inv) => (
                            <div key={inv.id} className="flex items-center justify-between rounded-lg border border-dashed border-gray-300 bg-white px-4 py-3">
                                <div>
                                    <div className="text-sm text-gray-700">{inv.email}</div>
                                    <div className="text-xs text-gray-500">
                                        {ROLE_LABELS[inv.role] || inv.role} · Vence {inv.expires_at}
                                        {inv.invited_by_name && ` · Invitado por ${inv.invited_by_name}`}
                                    </div>
                                    {inv.location_ids.length > 0 && (
                                        <div className="text-xs text-gray-400 mt-0.5">
                                            Sucursales: {inv.location_ids.map(lid => locations.find(l => l.id === lid)?.address.split(',')[0] ?? `#${lid}`).join(', ')}
                                        </div>
                                    )}
                                </div>
                                {isManager && (
                                    <button
                                        type="button"
                                        onClick={() => onCancelInvitation(inv.id)}
                                        disabled={isLoading}
                                        className="text-xs text-red-500 hover:text-red-600 bg-transparent border-none cursor-pointer disabled:opacity-50"
                                    >
                                        Cancelar
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={confirmRemove !== null}
                title="Quitar miembro"
                message={`¿Confirmás que querés quitar a "${members.find(m => m.id === confirmRemove)?.name ?? ''}" de la organización?`}
                confirmLabel="Sí, quitar"
                cancelLabel="No, mantener"
                loadingLabel="Quitando..."
                loading={isLoading}
                onConfirm={() => { if (confirmRemove) { onRemove(confirmRemove); setConfirmRemove(null); } }}
                onCancel={() => setConfirmRemove(null)}
            />

            {/* Location assignment modal */}
            {locationsMember && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-[fadeIn_150ms_ease-out]"
                    style={{ background: 'rgba(0,0,0,0.5)' }}
                    onClick={() => setLocationsMember(null)}
                >
                    <div
                        className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl animate-[slideUp_200ms_ease-out]"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            Sucursales de {locationsMember.name}
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">
                            Si no seleccionás ninguna, el miembro puede comprar desde cualquier sucursal.
                        </p>

                        <div className="space-y-2 mb-6">
                            {sortedLocations.map((loc) => (
                                <label key={loc.id} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={selectedLocIds.includes(loc.id)}
                                        onChange={(e) => {
                                            setSelectedLocIds(prev =>
                                                e.target.checked
                                                    ? [...prev, loc.id]
                                                    : prev.filter(id => id !== loc.id)
                                            );
                                        }}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                    />
                                    <div>
                                        <div className="text-sm font-medium text-gray-800">{loc.address.split(',')[0]}</div>
                                        {(loc.neighborhood || loc.company_name) && (
                                            <div className="text-xs text-gray-500">
                                                {[loc.company_name, loc.neighborhood].filter(Boolean).join(' · ')}
                                            </div>
                                        )}
                                    </div>
                                </label>
                            ))}
                        </div>

                        <div className="flex gap-2">
                            <Button
                                onClick={async () => {
                                    const result = await onUpdateLocations(locationsMember.id, selectedLocIds);
                                    if (result.success) setLocationsMember(null);
                                }}
                                disabled={isLoading}
                            >
                                Guardar
                            </Button>
                            <Button variant="secondary" onClick={() => setLocationsMember(null)} disabled={isLoading}>
                                Cancelar
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
